import { motion } from 'framer-motion';
import { Box, Container, Typography, useTheme } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';

const testimonials = [
  {
    quote:
      "I used BrainLoop to prep for my Step 1 boards and it completely changed how I study. The 'restudy wrong answers' feature alone saved me hours every week — I stopped wasting time on stuff I already knew.",
    name: 'Priya M.',
    role: 'Medical Student',
    stars: 5,
  },
  {
    quote:
      "I've tried Anki, Quizlet, all of them. BrainLoop is the first one that actually felt like it was working with me. The AI generates loops from my lecture notes in under a minute. It's become my daily study ritual.",
    name: 'Marcus T.',
    role: 'Law Student',
    stars: 5,
  },
  {
    quote:
      "I'm studying for a cloud certification while working full time. BrainLoop lets me get a focused 20-minute session in on my lunch break and actually retain it. The loop system just makes sense.",
    name: 'Dana K.',
    role: 'Software Engineer',
    stars: 5,
  },
];

export function TestimonialsSection() {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 10, md: 12 }, position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)',
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.72rem',
              display: 'block',
              mb: 2,
            }}
          >
            Real Learners
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            sx={{ fontWeight: 800, color: 'common.white', mb: 2 }}
          >
            What Students Are Saying
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: 500,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Thousands of learners use BrainLoop to prepare smarter, not longer.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 4,
          }}
        >
          {testimonials.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: index * 0.12 }}
            >
              <Box
                sx={{
                  p: { xs: 3.5, md: 4 },
                  borderRadius: '20px',
                  bgcolor: 'rgba(255,255,255,0.11)',
                  border: '1px solid rgba(255,255,255,0.16)',
                  boxShadow: [
                    '0 16px 40px rgba(0,0,0,0.4)',
                    '0 6px 12px rgba(0,0,0,0.25)',
                    'inset 0 2px 0 rgba(255,255,255,0.2)',
                    'inset 0 -2px 0 rgba(0,0,0,0.18)',
                  ].join(', '),
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.01)',
                    boxShadow: [
                      '0 24px 52px rgba(0,0,0,0.48)',
                      '0 8px 16px rgba(0,0,0,0.3)',
                      'inset 0 2px 0 rgba(255,255,255,0.22)',
                      'inset 0 -2px 0 rgba(0,0,0,0.2)',
                    ].join(', '),
                  },
                }}
              >
                {/* Stars */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <StarIcon
                      key={i}
                      sx={{ fontSize: 18, color: theme.palette.secondary.light }}
                    />
                  ))}
                </Box>

                {/* Quote mark */}
                <Typography
                  sx={{
                    fontSize: '3.5rem',
                    lineHeight: 0.6,
                    color: 'rgba(255,255,255,0.2)',
                    fontFamily: 'Georgia, serif',
                    userSelect: 'none',
                  }}
                >
                  "
                </Typography>

                {/* Quote text */}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255,255,255,0.85)',
                    lineHeight: 1.75,
                    flexGrow: 1,
                    mt: -1.5,
                  }}
                >
                  {t.quote}
                </Typography>

                {/* Attribution */}
                <Box sx={{ pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                    {t.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    {t.role}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
